import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { modulesAPI, progressAPI, questionsAPI } from "../services/api";
import {
  Box, AppBar, Toolbar, Typography, Button, Card, CardContent,
  Grid, Chip, LinearProgress, Avatar, CircularProgress, Alert
} from "@mui/material";
import SchoolIcon from "@mui/icons-material/School";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import LogoutIcon from "@mui/icons-material/Logout";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";



const STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "default", icon: <LockIcon /> },
  in_progress: { label: "In Progress", color: "warning", icon: <PlayCircleIcon /> },
  content_reviewed: { label: "Ready for Exam", color: "info", icon: <PlayCircleIcon /> },
  passed: { label: "Passed", color: "success", icon: <CheckCircleIcon /> },
  failed: { label: "Failed", color: "error", icon: <LockIcon /> },
};

export default function AgentDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [paths, setPaths] = useState([]);
  const [finalTemplates, setFinalTemplates] = useState({});
  const [progress, setProgress] = useState({});
  const [readiness, setReadiness] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const pathsRes = await modulesAPI.getPaths();
      setPaths(pathsRes.data);
      // Load final exam templates for each path
      const ftMap = {};
      for (const path of pathsRes.data) {
        try {
          const tmplRes = await modulesAPI.getModules(path.path_id);
          for (const mod of tmplRes.data) {
            const tRes = await questionsAPI.getTemplates(mod.module_id);
            const finalT = tRes.data.find(t => t.assessment_type === 'final');
            if (finalT) { ftMap[path.path_id] = finalT.template_id; break; }
          }
        } catch {}
      }
      setFinalTemplates(ftMap);
      const progressMap = {};
      const readinessMap = {};
      for (const path of pathsRes.data) {
        const [progRes, readRes] = await Promise.all([
          progressAPI.getMyProgress(path.path_id),
          progressAPI.getReadiness(path.path_id)
        ]);
        progressMap[path.path_id] = progRes.data.modules;
        readinessMap[path.path_id] = readRes.data;
      }
      setProgress(progressMap);
      setReadiness(readinessMap);
    } catch {
      setError("Failed to load training data. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  const getOverallProgress = (pathId) => {
    const mods = progress[pathId] || [];
    if (!mods.length) return 0;
    const passed = mods.filter(m => m.status === "passed").length;
    return Math.round(passed / mods.length * 100);
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa" }}>
      <AppBar position="static" sx={{ background: "linear-gradient(135deg, #1a237e, #0d47a1)" }}>
        <Toolbar>
          <img src="/alteram-logo.png" alt="Alteram" style={{ height: 36, objectFit: "contain", marginRight: 8, backgroundColor: "white", padding: "3px 6px", borderRadius: 4 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Alteram Contact Centre Training
          </Typography>
          <Typography variant="body2" sx={{ mr: 2, opacity: 0.9 }}>
            Welcome, {user?.full_name}
          </Typography>
          <Button color="inherit" onClick={() => navigate("/change-password")}
            startIcon={<LockIcon />} sx={{ mr: 1 }}>
            Change Password
          </Button>
          <Button color="inherit" onClick={logout} startIcon={<LogoutIcon />}>
            Sign Out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 4, maxWidth: 1100, mx: "auto" }}>
        <Typography variant="h4" fontWeight={700} color="#1a237e" mb={1}>
          My Training Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Track your progress and complete your assigned training modules
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress size={48} />
          </Box>
        ) : paths.length === 0 ? (
          <Card sx={{ borderRadius: 3, textAlign: "center", p: 4 }}>
            <SchoolIcon sx={{ fontSize: 64, color: "#ccc", mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No training programmes assigned yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contact your trainer to get enrolled
            </Typography>
          </Card>
        ) : (
          paths.map((path) => {
            const pathReadiness = readiness[path.path_id];
            const isCertified = pathReadiness?.status === "certified";
            const finalUnlocked = pathReadiness?.final_exam_unlocked;

            return (
              <Card key={path.path_id} sx={{ mb: 4, borderRadius: 3, boxShadow: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="#1a237e">
                        {path.path_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {path.description}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                      {isCertified && (
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                          <Chip
                            label="CERTIFIED ✓"
                            color="success"
                            icon={<EmojiEventsIcon />}
                            sx={{ fontWeight: 700, fontSize: 14, px: 1 }}
                          />
                          {(progress[path.path_id] || []).some(m => m.status === "not_started") && (
                            <Chip
                              label="📚 New modules added — complete to stay current"
                              sx={{ bgcolor: "#fff3e0", color: "#e65100", fontWeight: 600, fontSize: 11, border: "1px solid #e65100" }}
                              size="small"
                            />
                          )}
                        </Box>
                      )}
                      {finalUnlocked && !isCertified && (
                        <Button
                          variant="contained"
                          color="success"
                          size="large"
                          startIcon={<EmojiEventsIcon />}
                          onClick={() => { const tid = finalTemplates[path.path_id]; if (tid) navigate(`/exam/${tid}`); else alert("No final exam template found. Please contact your trainer."); }}
                          sx={{ fontWeight: 700, boxShadow: 3 }}
                        >
                          Take Final Exam
                        </Button>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Overall Progress</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {getOverallProgress(path.path_id)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={getOverallProgress(path.path_id)}
                      sx={{
                        height: 8, borderRadius: 4, bgcolor: "#e3f2fd",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: isCertified ? "#4caf50" : "#1a237e"
                        }
                      }}
                    />
                  </Box>

                  <Grid container spacing={2}>
                    {(progress[path.path_id] || []).filter(mod => mod.module_title !== "Final Exam").map((mod, index) => {
                      const config = STATUS_CONFIG[mod.status] || STATUS_CONFIG.not_started;
                      return (
                        <Grid item xs={12} md={6} key={mod.module_id}>
                          <Card variant="outlined" sx={{
                            borderRadius: 2, cursor: "pointer",
                            transition: "all 0.2s",
                            borderColor: mod.status === "passed" ? "#4caf50" : "#e0e0e0",
                            "&:hover": { boxShadow: 3, transform: "translateY(-2px)" }
                          }}
                            onClick={() => navigate(`/module/${mod.module_id}`)}
                          >
                            <CardContent sx={{ p: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <Avatar sx={{
                                  width: 32, height: 32, fontSize: 14, fontWeight: 700,
                                  bgcolor: mod.status === "passed" ? "#4caf50" : "#1a237e"
                                }}>
                                  {index + 1}
                                </Avatar>
                                <Typography variant="body1" fontWeight={600} sx={{ flexGrow: 1 }}>
                                  {mod.module_title}
                                </Typography>
                                <Chip label={config.label} color={config.color} size="small" />
                              </Box>
                              <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Video: {mod.video_percent}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Transcript: {mod.transcript_viewed ? "✓" : "Pending"}
                                </Typography>
                                {mod.latest_score && (
                                  <Typography variant="caption" color="text.secondary">
                                    Last score: {mod.latest_score}%
                                  </Typography>
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            );
          })
        )}
      </Box>
    </Box>
  );
}